/**
 * CDX Set-Auswahl (CO-4661, Baustein 2)
 *
 * Kacheln der verknüpften Set-Produkte verhalten sich wie Varianten:
 * - Klick: lädt die Produktseite des anderen Sets im Hintergrund und tauscht alle
 *   Template-Sections (Produktinfo, Akkordeons wie Lieferumfang/Inhaltsstoffe, USPs,
 *   Texte) in der laufenden Seite aus. Header, Footer und Drawer bleiben stehen.
 *   URL und Seitentitel werden nachgezogen.
 * - Hover (Desktop): legt das Bild des anderen Sets über die Galerie.
 */

if (!customElements.get('cdx-set-switcher')) {
  class CdxSetSwitcher extends HTMLElement {
    #abortController = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;
      this._sectionId = this.getAttribute('section-id');
      this._hover = this.hasAttribute('data-hover-preview') && window.matchMedia('(hover: hover)').matches;

      this.addEventListener('click', (event) => {
        const tile = event.target.closest('[data-url]');
        if (!tile) return;
        if (tile.classList.contains('is-current')) {
          event.preventDefault();
          return;
        }
        if (event.metaKey || event.ctrlKey || event.shiftKey) return; // neuer Tab bleibt möglich
        event.preventDefault();
        this._switchTo(tile);
      }, { signal });

      if (this._hover) {
        this.querySelectorAll('[data-url]').forEach((tile) => {
          tile.addEventListener('mouseenter', () => this._preview(tile), { signal });
          tile.addEventListener('mouseleave', () => this._clearPreview(), { signal });
        });
      }

      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this._preloadImages());
      } else {
        setTimeout(() => this._preloadImages(), 800);
      }
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this._clearPreview();
    }

    get _section() {
      return document.getElementById(`shopify-section-${this._sectionId}`);
    }

    get _viewParam() {
      return new URLSearchParams(window.location.search).get('view');
    }

    _preloadImages() {
      this.querySelectorAll('[data-image]').forEach((tile) => {
        if (tile.classList.contains('is-current')) return;
        const img = new Image();
        img.src = tile.dataset.image;
      });
    }

    /* ---------------- Hover-Vorschau ---------------- */

    _previewTarget() {
      return this._section?.querySelector('.product-gallery__media-list-wrapper') || this._section?.querySelector('product-gallery');
    }

    _preview(tile) {
      if (tile.classList.contains('is-current') || !tile.dataset.image) return;
      const target = this._previewTarget();
      if (!target) return;
      let overlay = target.querySelector('.cdx-ss__preview');
      if (!overlay) {
        overlay = document.createElement('img');
        overlay.className = 'cdx-ss__preview';
        overlay.alt = '';
        target.appendChild(overlay);
      }
      if (overlay.getAttribute('src') !== tile.dataset.image) overlay.src = tile.dataset.image;
      overlay.classList.add('is-visible');
    }

    _clearPreview() {
      document.querySelectorAll('.cdx-ss__preview.is-visible').forEach((overlay) => overlay.classList.remove('is-visible'));
    }

    /* ---------------- Wechsel ---------------- */

    async _switchTo(tile) {
      const url = tile.dataset.url;
      const view = this._viewParam;
      const nextUrl = view ? `${url}?view=${encodeURIComponent(view)}` : url;

      this.classList.add('is-loading');
      this._clearPreview();

      try {
        const response = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Alle Template-Sections austauschen, die es in beiden Seiten gibt.
        // Sections aus Section-Groups (Header, Footer, Overlay) bleiben unberührt.
        const mainSection = this._section;
        if (!mainSection) throw new Error('Section nicht gefunden');
        const top = mainSection.getBoundingClientRect().top + window.scrollY;

        const freshSections = doc.querySelectorAll('[id^="shopify-section-template--"]');
        let replaced = 0;
        freshSections.forEach((fresh) => {
          const current = document.getElementById(fresh.id);
          if (!current) return;
          current.innerHTML = fresh.innerHTML;
          current.className = fresh.className;
          this._runInlineScripts(current);
          replaced++;
        });
        if (replaced === 0) throw new Error('Keine Sections ersetzt');

        window.history.replaceState(window.history.state, '', nextUrl);
        if (doc.title) document.title = doc.title;

        if (window.scrollY > top) window.scrollTo({ top, behavior: 'auto' });
        document.documentElement.dispatchEvent(new CustomEvent('cdx:set-switched', { bubbles: true, detail: { url, productId: tile.dataset.productId } }));
      } catch (error) {
        console.error('[CDX Set-Auswahl] Wechsel fehlgeschlagen, lade Seite normal:', error);
        window.location.href = nextUrl;
      }
    }

    /** Inline-Scripts einer per innerHTML eingesetzten Section erneut ausführen (Custom-Elements laufen ohnehin). */
    _runInlineScripts(root) {
      root.querySelectorAll('script:not([src])').forEach((old) => {
        if (old.type && !['', 'text/javascript', 'module'].includes(old.type)) return; // z. B. application/json bleibt
        const script = document.createElement('script');
        [...old.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
        script.textContent = old.textContent;
        old.replaceWith(script);
      });
    }
  }

  customElements.define('cdx-set-switcher', CdxSetSwitcher);
}
